"use client";

import React, { useState } from "react";
import { Factory, Flame, Wind, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Cpu, Layers, SlidersHorizontal as Sliders } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface SectorItem {
  id: string;
  name: L6;
  badge: L6;
  icon: React.ComponentType<{
    className?: string;
  }>;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: L6;
  };
  overview: L6;
  operatingPressure: L6;
  recommendedAlloys: string[];
  recommendedMesh: L6;
  recommendedMeshTitle: L6;
  keyBenefits: L6[];
}
const SECTORS: SectorItem[] = [{
  id: "petrochemical",
  name: {
    en: "Petrochemical & Gas Refining",
    zh: "石化与燃气精炼",
    es: "Petroquímica y refinación de gas",
    de: "Petrochemie & Gasraffination",
    fr: "Pétrochimie et raffinage du gaz",
    pt: "Petroquímica e refino de gás"
  },
  badge: {
    en: "Extreme Temperature",
    zh: "耐极端高温",
    es: "Temperatura extrema",
    de: "Extremtemperatur",
    fr: "Température extrême",
    pt: "Temperatura extrema"
  },
  icon: Factory,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/petrochemical_filtration_mesh.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/petrochemical_filtration_mesh.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/petrochemical_filtration_mesh.webp-wvc-sizes",
    alt: {
      en: "Petrochemical gas refining filtration wire mesh assembly",
      zh: "石化燃气精炼过滤不锈钢丝网组件",
      es: "Conjunto de malla de alambre para filtración en refinación de gas petroquímico",
      de: "Drahtgewebebaugruppe für die petrochemische Gasraffinationsfiltration",
      fr: "Ensemble de toile métallique de filtration pour raffinage de gaz pétrochimique",
      pt: "Conjunto de tela metálica de filtração para refino de gás petroquímico"
    }
  },
  overview: {
    en: "High-temperature gas desulfurization, catalyst fines recovery, and hot process gas stream purification.",
    zh: "高温气体脱硫脱硝、催化剂微粉回收及高温工艺排气净化除尘。",
    es: "Desulfuración de gases a alta temperatura, recuperación de finos de catalizador y purificación de corrientes de gas caliente de proceso.",
    de: "Hochtemperatur-Gasentschwefelung, Rückgewinnung von Katalysatorfeinteilen und Heißgasstromreinigung.",
    fr: "Désulfuration des gaz à haute température, récupération des fines de catalyseur et purification des flux de gaz chauds de procédé.",
    pt: "Dessulfurização de gases a alta temperatura, recuperação de finos de catalisador e purificação de fluxos de gases quentes de processo."
  },
  operatingPressure: {
    en: "High ΔP",
    zh: "高压差",
    es: "Alto ΔP",
    de: "Hoher ΔP",
    fr: "Haut ΔP",
    pt: "Alto ΔP"
  },
  recommendedAlloys: ["316L SS", "310S SS", "Inconel 600"],
  recommendedMesh: {
    en: "5-Layer Sintered Wire Laminate (1µm – 25µm)",
    zh: "5层烧结金属丝网层压复合板（1µm – 25µm）",
    es: "Laminado de alambre sinterizado de 5 capas (1µm – 25µm)",
    de: "5-lagiges Sinterdrahtgewebe-Laminat (1µm – 25µm)",
    fr: "Laminé de toile frittée à 5 couches (1µm – 25µm)",
    pt: "Laminado de arame sinterizado de 5 camadas (1µm – 25µm)"
  },
  recommendedMeshTitle: {
    en: "5-Layer Sintered Wire Laminate",
    zh: "5层烧结金属丝网层压复合板",
    es: "Laminado de alambre sinterizado de 5 capas",
    de: "5-lagiges Sinterdrahtgewebe-Laminat",
    fr: "Laminé de toile frittée à 5 couches",
    pt: "Laminado de arame sinterizado de 5 camadas"
  },
  keyBenefits: [{
    en: "Thermal shock resistance during purge cycles",
    zh: "脉冲反吹吹扫循环中抗热震冲击性能优良",
    es: "Resistencia al choque térmico durante los ciclos de purga",
    de: "Thermoschockbeständigkeit während Spülzyklen",
    fr: "Résistance aux chocs thermiques lors des cycles de purge",
    pt: "Resistência ao choque térmico durante ciclos de purga"
  }, {
    en: "Zero fiber discharge under high turbulence",
    zh: "高紊流气流冲刷下绝无纤维介质脱落",
    es: "Cero desprendimiento de fibras bajo alta turbulencia",
    de: "Kein Faserabrieb bei hoher Turbulenz",
    fr: "Zéro rejet de fibres sous forte turbulence",
    pt: "Zero desprendimento de fibras sob alta turbulência"
  }, {
    en: "Repeated backwash cleanability",
    zh: "支持多次脉冲反吹与反洗再生复用",
    es: "Capacidad de lavado a contracorriente repetido",
    de: "Wiederholte Rückspülbarkeit und Reinigung",
    fr: "Nettoyabilité répétée par rétrolavage",
    pt: "Capacidade repetida de retrolavagem"
  }]
}, {
  id: "power-generation",
  name: {
    en: "Power Generation & Turbines",
    zh: "电力工业与燃气轮机",
    es: "Generación de energía y turbinas",
    de: "Energieerzeugung & Turbinen",
    fr: "Production d'énergie et turbines",
    pt: "Geração de energia e turbinas"
  },
  badge: {
    en: "High Velocity",
    zh: "高流速耐磨",
    es: "Alta velocidad",
    de: "Hochgeschwindigkeit",
    fr: "Haute vitesse",
    pt: "Alta velocidade"
  },
  icon: Flame,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/power_gen_gas_filtration.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/power_gen_gas_filtration.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/power_gen_gas_filtration.webp-wvc-sizes",
    alt: {
      en: "Power generation turbine exhaust gas filtration mesh panel",
      zh: "发电燃气轮机排气过滤不锈钢丝网滤板",
      es: "Panel de malla de filtración de gases de escape de turbina de generación eléctrica",
      de: "Drahtgewebepanel für Abgasfiltration bei Gasturbinen zur Stromerzeugung",
      fr: "Panneau de toile métallique de filtration des gaz d'échappement de turbine",
      pt: "Painel de tela de filtração de gases de exaustão de turbina de geração de energia"
    }
  },
  overview: {
    en: "Turbine air inlet intake protection, flue gas particulate separation, and hot syngas conditioning.",
    zh: "燃气轮机进气吸入保护、高温烟气微粒分离及热合成气调质净化。",
    es: "Protección de admisión de aire de turbina, separación de partículas de gases de combustión y acondicionamiento de gas de síntesis caliente.",
    de: "Lufteinlassschutz für Turbinen, Rauchgaspartikelabscheidung und Aufbereitung von heißem Synthesegas.",
    fr: "Protection de l'admission d'air de turbine, séparation des particules de fumées et conditionnement du gaz de synthèse chaud.",
    pt: "Proteção de admissão de ar de turbina, separação de partículas de gases de exaustão e condicionamento de gás de síntese quente."
  },
  operatingPressure: {
    en: "Medium to High Flow Rates",
    zh: "中高气体流量工况",
    es: "Caudales medios a altos",
    de: "Mittlere bis hohe Durchflussraten",
    fr: "Débits moyens à élevés",
    pt: "Vazões médias a altas"
  },
  recommendedAlloys: ["316L SS", "Hastelloy C-276"],
  recommendedMesh: {
    en: "Dutch Weave Heavy Wire Cloth (5µm – 50µm)",
    zh: "重型密纹荷兰编织网布（5µm – 50µm）",
    es: "Tela metálica Dutch pesada (5µm – 50µm)",
    de: "Schwere Tressengewebe (5µm – 50µm)",
    fr: "Toile tissage hollandais lourde (5µm – 50µm)",
    pt: "Tela de arame Dutch pesada (5µm – 50µm)"
  },
  recommendedMeshTitle: {
    en: "Dutch Weave Heavy Wire Cloth",
    zh: "重型密纹荷兰编织网布",
    es: "Tela metálica Dutch pesada",
    de: "Schwere Tressengewebe",
    fr: "Toile tissage hollandais lourde",
    pt: "Tela de arame Dutch pesada"
  },
  keyBenefits: [{
    en: "Low structural differential pressure drop",
    zh: "结构流阻压降低，提升气流动能效率",
    es: "Baja caída de presión diferencial estructural",
    de: "Geringer struktureller Differenzdruckverlust",
    fr: "Faible perte de charge différentielle structurelle",
    pt: "Baixa queda de pressão diferencial estrutural"
  }, {
    en: "Erosion-resistant weave geometry",
    zh: "密实耐磨几何编织结构，抵抗高速气固冲刷",
    es: "Geometría de tejido resistente a la erosión",
    de: "Erosionsbeständige Gewebegeometrie",
    fr: "Géométrie de tissage résistante à l'érosion",
    pt: "Geometria de trama resistente à erosão"
  }, {
    en: "Extended service life in corrosive exhaust streams",
    zh: "在腐蚀性烟气流中具备持久使用寿命",
    es: "Vida útil prolongada en corrientes de escape corrosivas",
    de: "Verlängerte Standzeit in korrosiven Abgasströmen",
    fr: "Durée de vie prolongée dans les flux d'échappement corrosifs",
    pt: "Vida útil prolongada em fluxos de exaustão corrosivos"
  }]
}, {
  id: "industrial-gas",
  name: {
    en: "Industrial Gas & Cryogenics",
    zh: "工业气体与深冷空分",
    es: "Gases industriales y criogenia",
    de: "Industriegase & Kryotechnik",
    fr: "Gaz industriels et cryogénie",
    pt: "Gases industriais e criogenia"
  },
  badge: {
    en: "Ultra-Clean",
    zh: "超洁净级",
    es: "Ultra limpio",
    de: "Ultra-Rein",
    fr: "Ultra-propre",
    pt: "Ultra limpo"
  },
  icon: Wind,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/cryogenic_gas_filter_mesh.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/cryogenic_gas_filter_mesh.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/cryogenic_gas_filter_mesh.webp-wvc-sizes",
    alt: {
      en: "Industrial cryogenic oxygen and nitrogen filtration mesh elements",
      zh: "工业深冷液氧与高纯氮气过滤不锈钢丝网元件",
      es: "Elementos de malla para filtración de oxígeno y nitrógeno criogénico industrial",
      de: "Drahtgewebe-Filterelemente für industrielle kryogene Sauerstoff- und Stickstofffiltration",
      fr: "Éléments en toile métallique pour filtration cryogénique industrielle d'oxygène et d'azote",
      pt: "Elementos de tela para filtração criogênica de oxigênio e nitrogênio industrial"
    }
  },
  overview: {
    en: "Pure oxygen, nitrogen, and argon gas filtration, vaporisation screening, and cryogenic fluid strainers.",
    zh: "高纯氧气、氮气与氩气精密过滤、气化过滤筛分及低温深冷介质滤芯。",
    es: "Filtración de oxígeno, nitrógeno y argón puros, tamizado en vaporización y filtros para fluidos criogénicos.",
    de: "Filtration von reinem Sauerstoff, Stickstoff und Argon, Verdampfungssiebung und Schmutzfänger für Tieftemperaturflüssigkeiten.",
    fr: "Filtration d'oxygène, d'azote et d'argon purs, criblage en vaporisation et crépines pour fluides cryogéniques.",
    pt: "Filtração de oxigênio, nitrogênio e argônio puros, peneiramento em vaporização e filtros de fluidos criogênicos."
  },
  operatingPressure: {
    en: "Ultra-High Pressure",
    zh: "超高压力",
    es: "Presión ultra alta",
    de: "Ultra-Hochdruck",
    fr: "Ultra-haute pression",
    pt: "Pressão ultra alta"
  },
  recommendedAlloys: ["316L SS EP", "Monel 400"],
  recommendedMesh: {
    en: "Calendered Sintered Dutch Mesh (0.5µm – 10µm)",
    zh: "轧光烧结荷兰编织网（0.5µm – 10µm）",
    es: "Malla Dutch sinterizada y calandrada (0.5µm – 10µm)",
    de: "Kalandriertes Sinter-Tressengewebe (0.5µm – 10µm)",
    fr: "Toile hollandaise frittée et calandrée (0.5µm – 10µm)",
    pt: "Malha Dutch sinterizada e calandrada (0.5µm – 10µm)"
  },
  recommendedMeshTitle: {
    en: "Calendered Sintered Dutch Mesh",
    zh: "轧光烧结荷兰编织网",
    es: "Malla Dutch sinterizada y calandrada",
    de: "Kalandriertes Sinter-Tressengewebe",
    fr: "Toile hollandaise frittée et calandrée",
    pt: "Malha Dutch sinterizada e calandrada"
  },
  keyBenefits: [{
    en: "Cleaned and degreased for oxygen and high-purity service",
    zh: "严格脱脂清洗，符合氧气与高纯介质使用要求",
    es: "Limpieza y desengrase para servicio de oxígeno y alta pureza",
    de: "Entfettet und gereinigt für Sauerstoff- und Reinstgaseinsatz",
    fr: "Dégraissé et nettoyé pour service oxygène et haute pureté",
    pt: "Desengraxado e limpo para serviço de oxigênio e alta pureza"
  }, {
    en: "Burst-proof structural support mesh layering",
    zh: "抗爆结构增强支撑层，防止高压冲击失效",
    es: "Capas de soporte estructural a prueba de rotura",
    de: "Berstsichere Stützgewebelagen",
    fr: "Couches de support structurel antidéflagrantes",
    pt: "Camadas de suporte estrutural à prova de ruptura"
  }, {
    en: "No organic binders or particle contamination",
    zh: "无有机粘合剂，彻底避免二次微粒污染",
    es: "Sin aglutinantes orgánicos ni contaminación por partículas",
    de: "Keine organischen Bindemittel oder Partikelkontamination",
    fr: "Sans liant organique ni contamination particulaire",
    pt: "Sem aglutinantes orgânicos ou contaminação por partículas"
  }]
}, {
  id: "aerospace",
  name: {
    en: "Aerospace & Defense Environmental",
    zh: "航空航天与国防环控",
    es: "Aeroespacial y defensa ambiental",
    de: "Luft- und Raumfahrt & Verteidigung",
    fr: "Aérospatiale et défense environnementale",
    pt: "Aeroespacial e defesa ambiental"
  },
  badge: {
    en: "Vibration Resistant",
    zh: "高抗震防疲劳",
    es: "Resistente a vibraciones",
    de: "Vibrationsbeständig",
    fr: "Résistant aux vibrations",
    pt: "Resistente a vibrações"
  },
  icon: Cpu,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/aerospace_gas_mesh_component.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/aerospace_gas_mesh_component.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/aerospace_gas_mesh_component.webp-wvc-sizes",
    alt: {
      en: "Aerospace environmental control bleed air filtration mesh screen",
      zh: "航空航天环控系统引气过滤精密丝网滤芯",
      es: "Malla filtrante de aire de sangrado para control ambiental aeroespacial",
      de: "Drahtgewebesieb für Zapfluftfiltration in der Luftfahrt-Umweltkontrolle",
      fr: "Tamis filtrant d'air de prélèvement pour régulation environnementale aérospatiale",
      pt: "Tela de arame para filtração de ar de sangria para controle ambiental aeroespacial"
    }
  },
  overview: {
    en: "Cabin air conditioning bleed air filters, rocket propellant gas purging, and pneumatic system strainers.",
    zh: "机舱环控空调引气过滤、推进剂吹扫气路杂质截留及气动系统精密滤网。",
    es: "Filtros de aire de sangrado para aire acondicionado de cabina, purga de gases propulsores y filtros de sistemas neumáticos.",
    de: "Kabinenklimaanlagen-Zapfluftfilter, Treibstoffgas-Spülung und Pneumatik-Schmutzfänger.",
    fr: "Filtres d'air de prélèvement pour conditionnement d'air cabine, purge des gaz propulseurs et filtres pneumatiques.",
    pt: "Filtros de ar de sangria para cabine, purga de gases propulsores e filtros para sistemas pneumáticos."
  },
  operatingPressure: {
    en: "Pneumatic Peak Spikes",
    zh: "耐受气动脉冲峰值冲击",
    es: "Picos de presión neumática",
    de: "Pneumatische Druckspitzen",
    fr: "Pics de pression pneumatique",
    pt: "Picos de pressão pneumática"
  },
  recommendedAlloys: ["Titanium Gr 2", "316L SS"],
  recommendedMesh: {
    en: "Precision Sintered Wire Mesh Elements (2µm – 15µm)",
    zh: "精密烧结金属网滤芯元件（2µm – 15µm）",
    es: "Elementos de malla de alambre sinterizada de precisión (2µm – 15µm)",
    de: "Präzisions-Sinterdrahtgewebeelemente (2µm – 15µm)",
    fr: "Éléments de toile métallique frittée de précision (2µm – 15µm)",
    pt: "Elementos de tela metálica sinterizada de precisão (2µm – 15µm)"
  },
  recommendedMeshTitle: {
    en: "Precision Sintered Wire Mesh Elements",
    zh: "精密烧结金属网滤芯元件",
    es: "Elementos de malla de alambre sinterizada de precisión",
    de: "Präzisions-Sinterdrahtgewebeelemente",
    fr: "Éléments de toile métallique frittée de précision",
    pt: "Elementos de tela metálica sinterizada de precisão"
  },
  keyBenefits: [{
    en: "High strength-to-weight structural integrity",
    zh: "高强度重量比，结构紧凑坚固",
    es: "Alta relación resistencia-peso e integridad estructural",
    de: "Hohes Festigkeits-Gewichts-Verhältnis",
    fr: "Haute intégrité structurelle rapport résistance/poids",
    pt: "Alta relação resistência-peso e integridade estrutural"
  }, {
    en: "Resistant to high vibration fatigue",
    zh: "耐受强震动疲劳，长期运行不开裂",
    es: "Resistente a la fatiga por alta vibración",
    de: "Beständig gegen hochfrequente Vibrationsermüdung",
    fr: "Résistant à la fatigue par fortes vibrations",
    pt: "Resistente à fadiga por alta vibração"
  }, {
    en: "Controlled aperture tolerances conforming to ASTM E2016",
    zh: "严苛执行 ASTM E2016 精密公差控制",
    es: "Tolerancias de apertura controladas conforme a ASTM E2016",
    de: "Präzise Maschentoleranzen nach ASTM E2016",
    fr: "Tolérances d'ouverture contrôlées selon ASTM E2016",
    pt: "Tolerâncias de abertura controladas conforme ASTM E2016"
  }]
}, {
  id: "pharma",
  name: {
    en: "Pharmaceutical & Sterile Venting",
    zh: "制药工业与无菌呼吸过滤",
    es: "Farmacéutica y venteo estéril",
    de: "Pharmazie & sterile Belüftung",
    fr: "Pharmaceutique et évents stériles",
    pt: "Farmacêutica e respiro estéril"
  },
  badge: {
    en: "Sanitary Grade",
    zh: "卫生级应用",
    es: "Grado sanitario",
    de: "Sanitärgüte",
    fr: "Qualité sanitaire",
    pt: "Grau sanitário"
  },
  icon: ShieldCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png-wvc-sizes",
    alt: {
      en: "Pharmaceutical sterile vent air stainless steel mesh filter cartridge",
      zh: "制药发酵罐无菌呼吸空气不锈钢丝网滤筒",
      es: "Cartucho de filtro de malla de acero inoxidable para venteo de aire estéril farmacéutico",
      de: "Edelstahldrahtgewebe-Filterkerze für sterile Belüftung in der Pharmazie",
      fr: "Cartouche filtrante en toile d'acier inoxydable pour évent d'air stérile pharmaceutique",
      pt: "Cartucho filtrante de malha de aço inoxidável para respiro de ar estéril farmacêutico"
    }
  },
  overview: {
    en: "Sterile air reactor venting, compressed process gas filtration, and powder conveying gas separation.",
    zh: "生物反应器排气呼吸口、压缩工艺气体过滤及气力粉体输送气固分离。",
    es: "Venteo de aire en reactores, filtración de gases comprimidos y separación de gas en transporte de polvo.",
    de: "Reaktorbelüftung, Druckgasfiltration und Gastrennung bei pneumatischer Pulverförderung.",
    fr: "Évents pour réacteurs, filtration de gaz comprimés et séparation gaz-solide pour convoyage de poudres.",
    pt: "Respiro em reatores, filtração de gases comprimidos e separação gás-sólido em transporte de pó."
  },
  operatingPressure: {
    en: "Low to Medium ΔP",
    zh: "中低压差运行",
    es: "ΔP bajo a medio",
    de: "Niedriger bis mittlerer ΔP",
    fr: "ΔP faible à moyen",
    pt: "ΔP baixo a médio"
  },
  recommendedAlloys: ["316L SS"],
  recommendedMesh: {
    en: "Twill Dutch Weave Filter Cloth (1µm – 5µm)",
    zh: "斜纹荷兰密纹编织滤布（1µm – 5µm）",
    es: "Tela filtrante de tejido Dutch cruzado (1µm – 5µm)",
    de: "Köpertressengewebe-Filtertuch (1µm – 5µm)",
    fr: "Toile filtrante tissage hollandais sergé (1µm – 5µm)",
    pt: "Tela filtrante de trama holandesa sarja (1µm – 5µm)"
  },
  recommendedMeshTitle: {
    en: "Twill Dutch Weave Filter Cloth",
    zh: "斜纹荷兰密纹编织滤布",
    es: "Tela filtrante de tejido Dutch cruzado",
    de: "Köpertressengewebe-Filtertuch",
    fr: "Toile filtrante tissage hollandais sergé",
    pt: "Tela filtrante de trama holandesa sarja"
  },
  keyBenefits: [{
    en: "High-cleanliness 316L stainless steel construction",
    zh: "高洁净度 316L 不锈钢金属结构",
    es: "Construcción en acero inoxidable 316L de alta limpieza",
    de: "Hochreine Edelstahlkonstruktion aus 316L",
    fr: "Construction en acier inoxydable 316L haute propreté",
    pt: "Construção em aço inoxidável 316L de alta pureza"
  }, {
    en: "Repeated steam-in-place (SIP) compatibility",
    zh: "耐受多次在线原位蒸汽灭菌（SIP）与清洗（CIP）",
    es: "Compatibilidad con vapor in situ repetido (SIP)",
    de: "Beständig gegen wiederholte SIP-Dampfsterilisation",
    fr: "Compatibilité avec la stérilisation à la vapeur en place (SIP)",
    pt: "Compatibilidade com esterilização por vapor no local (SIP)"
  }, {
    en: "Smooth pore structure with low particulate retention drag",
    zh: "平滑孔隙结构，流阻低且易于反吹清洗",
    es: "Estructura de poros lisa con baja resistencia al flujo",
    de: "Glatte Porenstruktur mit geringem Strömungswiderstand",
    fr: "Structure de pores lisse à faible perte de charge",
    pt: "Estrutura de poros lisa com baixa perda de carga"
  }]
}, {
  id: "semiconductor",
  name: {
    en: "Semiconductor & Process Gas",
    zh: "半导体制造与高纯工艺气体",
    es: "Semiconductores y gases de proceso",
    de: "Halbleiter & Prozessgase",
    fr: "Semi-conducteurs et gaz de procédé",
    pt: "Semicondutores e gases de processo"
  },
  badge: {
    en: "High Purity",
    zh: "高纯等级",
    es: "Alta pureza",
    de: "Reinstgas-Güte",
    fr: "Haute pureté",
    pt: "Alta pureza"
  },
  icon: Layers,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/semiconductor_gas_mesh.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/semiconductor_gas_mesh.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/semiconductor_gas_mesh.webp-wvc-sizes",
    alt: {
      en: "Ultra high purity semiconductor gas filtration mesh element",
      zh: "超高纯半导体工艺气体精密过滤丝网元件",
      es: "Elemento de malla para filtración de gases de ultra alta pureza en semiconductores",
      de: "Drahtgewebeelement für Ultra-Reinstgasfiltration in der Halbleiterindustrie",
      fr: "Élément en toile métallique pour filtration de gaz ultra-haute pureté pour semi-conducteurs",
      pt: "Elemento de tela para filtração de gases de ultra-alta pureza em semicondutores"
    }
  },
  overview: {
    en: "Process gas line particle retention and chemical vapor deposition (CVD) air diffusers.",
    zh: "工艺管线微粒截留、化学气相沉积（CVD）气流扩散器及气体过滤。",
    es: "Retención de partículas en líneas de gas de proceso y difusores de aire CVD.",
    de: "Partikelrückhaltung in Prozessgasleitungen und CVD-Gasdiffusoren.",
    fr: "Rétention de particules sur lignes de gaz de procédé et diffuseurs CVD.",
    pt: "Retenção de partículas em linhas de gases de processo e difusores CVD."
  },
  operatingPressure: {
    en: "Vacuum to High Pressure",
    zh: "高真空至高压运行",
    es: "Vacío hasta alta presión",
    de: "Vakuum bis Hochdruck",
    fr: "Vide à haute pression",
    pt: "Vácuo até alta pressão"
  },
  recommendedAlloys: ["316L SS", "Hastelloy C-22"],
  recommendedMesh: {
    en: "Multi-layer Micro-Porously Sintered Laminate",
    zh: "多层微孔烧结层压板",
    es: "Laminado sinterizado microporoso multicapa",
    de: "Mehrlagiges mikroporöses Sinterlaminat",
    fr: "Laminé fritté microporeux multicouche",
    pt: "Laminado sinterizado microporoso multicamadas"
  },
  recommendedMeshTitle: {
    en: "Multi-layer Micro-Porously Sintered Laminate",
    zh: "多层微孔烧结层压板",
    es: "Laminado sinterizado microporoso multicapa",
    de: "Mehrlagiges mikroporöses Sinterlaminat",
    fr: "Laminé fritté microporeux multicouche",
    pt: "Laminado sinterizado microporoso multicamadas"
  },
  keyBenefits: [{
    en: "Cleanroom degreased and packaged upon request",
    zh: "可按需提供洁净清洗与专用封装",
    es: "Desengrasado y empaquetado en sala limpia bajo pedido",
    de: "Auf Anfrage im Reinraum entfettet und verpackt",
    fr: "Dégraissé et conditionné en salle propre sur demande",
    pt: "Desengraxado e embalado em sala limpa sob consulta"
  }, {
    en: "Sub-micron particulate trapping capability",
    zh: "具备亚微米级微粒高效截留拦截能力",
    es: "Capacidad de retención de partículas submicrónicas",
    de: "Abscheidung submikroner Partikel",
    fr: "Capacité de piégeage des particules submicroniques",
    pt: "Capacidade de retenção de partículas submicrométricas"
  }, {
    en: "Minimal outgassing under hard vacuum",
    zh: "高真空环境下极低气体析出率",
    es: "Mínima desgasificación bajo alto vacío",
    de: "Minimale Ausgasung im Hochvakuum",
    fr: "Dégazage minimal sous vide poussé",
    pt: "Mínima desgaseificação sob alto vácuo"
  }]
}];
const copy = {
  eyebrow: {
    en: "Gas & Air Filtration Sector Solutions",
    zh: "气体与空气过滤行业解决方案",
    es: "Soluciones sectoriales de filtración de gas y aire",
    de: "Branchenlösungen für Gas- und Luftfiltration",
    fr: "Solutions sectorielles pour filtration des gaz et de l'air",
    pt: "Soluções setoriais de filtração de gases e ar"
  },
  h2: {
    en: "Engineered Wire Mesh for Critical Sector Demands",
    zh: "针对严苛工况量身定制的高性能金属丝网",
    es: "Malla de alambre diseñada para demandas críticas del sector",
    de: "Drahtgewebe nach Maß für anspruchsvolle Branchenanforderungen",
    fr: "Toiles métalliques conçues pour les exigences critiques des secteurs",
    pt: "Malhas de arame projetadas para demandas críticas do setor"
  },
  lead: {
    en: "MutualFilter manufactures heavy-duty woven and sintered stainless steel wire mesh assemblies tailored to high-temperature, corrosive, and high-velocity gas and air streams across industrial sectors.",
    zh: "MutualFilter 研发制造耐高温、抗腐蚀、耐高流速冲刷的重型编织与多层烧结不锈钢丝网组件，广泛服务于关键工业领域的高标准气体净化系统。",
    es: "MutualFilter fabrica conjuntos de malla de alambre tejida y sinterizada de acero inoxidable adaptados a corrientes de gas y aire a alta temperatura, corrosivas y de alta velocidad.",
    de: "MutualFilter fertigt hochbelastbare Gewebe- und Sinterdrahtbaugruppen aus Edelstahl für korrosive und hochtemperaturbeständige Gas- und Luftströme in weltweiten Industriezweigen.",
    fr: "MutualFilter fabrique des ensembles de toiles métalliques tissées et frittées en acier inoxydable pour les flux de gaz et d'air corrosifs, haute température et grande vitesse.",
    pt: "A MutualFilter fabrica conjuntos de malhas de arame tecidas e sinterizadas em aço inoxidável para fluxos corrosivos, de alta temperatura e alta velocidade de gases e ar."
  },
  specMatrix: {
    en: "SPEC MATRIX",
    zh: "规格矩阵",
    es: "MATRIZ DE ESPEC.",
    de: "SPEZIFIKATION",
    fr: "MATRICE SPEC",
    pt: "MATRIZ DE ESPEC."
  },
  recMeshGeometry: {
    en: "Recommended Mesh Geometry",
    zh: "推荐丝网几何结构",
    es: "Geometría de malla recomendada",
    de: "Empfohlene Gewebegeometrie",
    fr: "Géométrie de toile recommandée",
    pt: "Geometria de malha recomendada"
  },
  pressureClassLabel: {
    en: "Pressure Class",
    zh: "压力等级",
    es: "Clase de presión",
    de: "Druckklasse",
    fr: "Classe de pression",
    pt: "Classe de pressão"
  },
  alloysLabel: {
    en: "Alloys",
    zh: "推荐合金",
    es: "Aleaciones",
    de: "Legierungen",
    fr: "Alliages",
    pt: "Ligas"
  },
  keyAttributesTitle: {
    en: "Key Performance Attributes:",
    zh: "关键性能优势：",
    es: "Atributos clave de rendimiento:",
    de: "Wichtigste Leistungsmerkmale:",
    fr: "Principaux atouts de performance :",
    pt: "Principais atributos de desempenho:"
  },
  cardInlineNote: {
    en: "Custom aperture sizing & pleating configurations available upon request.",
    zh: "可按需提供定制孔径尺寸与波纹折叠结构加工。",
    es: "Tamaños de apertura y configuraciones plisadas personalizadas disponibles a pedido.",
    de: "Kundenspezifische Maschenweiten und Plissierkonfigurationen auf Anfrage erhältlich.",
    fr: "Tailles d'ouverture et plissages sur mesure disponibles sur demande.",
    pt: "Tamanhos de abertura e configurações plissadas personalizadas disponíveis sob consulta."
  },
  overviewTitle: {
    en: "Full Sector Technical Overview",
    zh: "全部行业技术规格概览",
    es: "Resumen técnico completo del sector",
    de: "Vollständige technische Branchenübersicht",
    fr: "Vue d'ensemble technique complète par secteur",
    pt: "Visão geral técnica completa do setor"
  },
  overviewSubtitle: {
    en: "Compare stainless wire mesh filtration parameters across key industrial sectors.",
    zh: "对比各关键工业领域的气体与空气不锈钢丝网过滤参数与适配材质。",
    es: "Compare los parámetros de filtración de malla de acero inoxidable en sectores clave.",
    de: "Vergleichen Sie Parameter für Edelstahldrahtgewebe in wichtigen Industriezweigen.",
    fr: "Comparez les paramètres de filtration en toile métallique inox selon les secteurs industriels.",
    pt: "Compare os parâmetros de filtração de telas de aço inoxidável nos principais setores."
  },
  ctaViewDatasheets: {
    en: "View Mesh Datasheets",
    zh: "查看丝网技术资料",
    es: "Ver fichas técnicas",
    de: "Datenblätter anzeigen",
    fr: "Voir les fiches techniques",
    pt: "Ver fichas técnicas"
  },
  meshStructureLabel: {
    en: "Mesh Structure:",
    zh: "丝网结构：",
    es: "Estructura de malla:",
    de: "Gewebestruktur:",
    fr: "Structure de toile :",
    pt: "Estrutura da malha:"
  },
  exploreSpec: {
    en: "Explore Sector Spec",
    zh: "查看该行业规格",
    es: "Explorar especificaciones",
    de: "Branchenspezifikation prüfen",
    fr: "Explorer les spécifications",
    pt: "Explorar especificações"
  }
};
export default function SectorSolutions() {
  const [activeSector, setActiveSector] = useState<string>("petrochemical");
  const {
    lang
  } = useLanguage();
  const selectedData = SECTORS.find(s => s.id === activeSector) || SECTORS[0];
  return <section id="sectorsolutions" data-nav="light" className="relative bg-muted/50 text-foreground mf-section overflow-hidden border-b border-border">
      <div className="mf-container relative z-10">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-card text-foreground mf-caption font-semibold tracking-wider uppercase border border-border">
              <Sliders className="size-3.5 text-primary" />
              <span>{pick(lang, copy.eyebrow)}</span>
            </div>

            <h2 className="mf-h2 text-foreground font-default">
              {pick(lang, copy.h2)}
            </h2>

            <p className="text-muted-foreground mf-lead max-w-[65ch] mx-auto">
              {pick(lang, copy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Sector Quick-Select Tabs */}
        <Reveal delay={80}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            {SECTORS.map(sector => {
            const IconComp = sector.icon;
            const isActive = sector.id === activeSector;
            return <Button key={sector.id} type="button" variant={isActive ? "default" : "outline"} onClick={() => setActiveSector(sector.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 h-auto rounded-lg text-sm font-medium transition-colors cursor-pointer ${isActive ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-border hover:bg-muted hover:border-border"}`}>
                  <IconComp className={`size-4 ${isActive ? "text-primary-foreground" : "text-muted-foreground"}`} />
                  <span>{pick(lang, sector.name)}</span>
                </Button>;
          })}
          </div>
        </Reveal>

        {/* Featured Sector Detail Box */}
        <Reveal delay={120}>
          <div className="mt-10">
            <Card className="bg-card border-border overflow-hidden rounded-lg">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                {/* Image & Visual Spec Panel */}
                <div className="mf-card-media lg:col-span-5 relative flex flex-col justify-between p-6 sm:p-8 min-h-[320px] lg:min-h-full">
                  <img src={selectedData.image.src} data-wvc-srcset={selectedData.image.srcset} data-wvc-sizes={selectedData.image.sizes} alt={pick(lang, selectedData.image.alt)} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover object-center" />

                  <div className="relative z-10 flex justify-between items-start">
                    <Badge variant="outline" className="bg-card text-foreground border border-border font-semibold px-3 py-1 mf-caption">
                      {pick(lang, selectedData.badge)}
                    </Badge>
                    <span className="mf-caption font-mono font-medium text-foreground bg-card border border-border px-2.5 py-1 rounded">
                      {pick(lang, copy.specMatrix)}
                    </span>
                  </div>

                  <div className="relative z-10 mt-auto pt-16">
                    <div className="bg-card border border-border rounded-lg p-4 space-y-1">
                      <span className="mf-caption font-medium tracking-wider uppercase text-muted-foreground block">
                        {pick(lang, copy.recMeshGeometry)}
                      </span>
                      <h3 className="mf-h3 font-bold text-foreground font-default">{pick(lang, selectedData.recommendedMesh)}</h3>
                    </div>
                  </div>
                </div>

                {/* Technical Specifications & Benefits */}
                <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-between space-y-8">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {React.createElement(selectedData.icon, {
                      className: "size-6 text-primary shrink-0"
                    })}
                      <h3 className="mf-h3 font-bold text-foreground font-default">
                        {pick(lang, selectedData.name)}
                      </h3>
                    </div>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-2 max-w-[65ch]">
                      {pick(lang, selectedData.overview)}
                    </p>

                    {/* Conditions & Alloy Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 p-4 rounded-lg bg-muted/60 border border-border">
                      <div className="space-y-1">
                        <span className="mf-caption font-medium text-muted-foreground uppercase tracking-wider block">
                          {pick(lang, copy.pressureClassLabel)}
                        </span>
                        <span className="text-sm font-semibold text-foreground block">
                          {pick(lang, selectedData.operatingPressure)}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <span className="mf-caption font-medium text-muted-foreground uppercase tracking-wider block">
                          {pick(lang, copy.alloysLabel)}
                        </span>
                        <span className="text-sm font-semibold text-foreground block">{selectedData.recommendedAlloys.join(", ")}</span>
                      </div>
                    </div>

                    {/* Core Technical Benefits */}
                    <div className="mt-6 space-y-3">
                      <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                        {pick(lang, copy.keyAttributesTitle)}
                      </h4>
                      <ul className="grid grid-cols-1 gap-2.5">
                        {selectedData.keyBenefits.map((benefit, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{pick(lang, benefit)}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>

                  {/* Inline Note Row */}
                  <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                    <div className="mf-caption text-muted-foreground">
                      {pick(lang, copy.cardInlineNote)}
                    </div>
                    <Button asChild variant="outline" size="sm" className="gap-2 font-medium shrink-0 self-start sm:self-auto">
                      <Link to="/RequestAQuotePage">
                        <span>{pick(lang, COMMON.requestQuote)}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </Reveal>

        {/* 6-Grid Summary Cards for All Sectors */}
        <div className="mt-16">
          <Reveal>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="mf-h3 font-bold text-foreground font-default">
                  {pick(lang, copy.overviewTitle)}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground max-w-[65ch] mt-1">
                  {pick(lang, copy.overviewSubtitle)}
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex gap-1.5">
                <Link to="/TechnicalResourcesPage">
                  <span>{pick(lang, copy.ctaViewDatasheets)}</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTORS.map((sec, i) => {
            const SecIcon = sec.icon;
            return <Reveal key={sec.id} delay={Math.min(i * 60, 300)}>
                  <Card className={`bg-card text-card-foreground border-border rounded-lg flex flex-col justify-between h-full ${sec.id === activeSector ? "border-primary" : ""}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="p-2 rounded-md bg-muted text-primary border border-border">
                          <SecIcon className="size-5 text-primary" />
                        </div>
                        <Badge variant="outline" className="mf-caption font-mono">
                          {pick(lang, sec.badge)}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-bold font-default">
                        {pick(lang, sec.name)}
                      </CardTitle>
                      <CardDescription className="mf-caption line-clamp-2 mt-1">
                        {pick(lang, sec.overview)}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 space-y-4">
                      <div className="p-3 rounded-md bg-muted/50 mf-caption space-y-1.5 border border-border/60">
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-muted-foreground shrink-0">
                            {pick(lang, copy.meshStructureLabel)}
                          </span>
                          <span className="font-semibold text-foreground text-right truncate">
                            {pick(lang, sec.recommendedMeshTitle)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-muted-foreground shrink-0">
                            {pick(lang, copy.pressureClassLabel)}:
                          </span>
                          <span className="font-semibold text-foreground text-right truncate">
                            {pick(lang, sec.operatingPressure)}
                          </span>
                        </div>
                      </div>

                      <Button variant="ghost" size="sm" onClick={() => setActiveSector(sec.id)} className="w-full justify-between mf-caption font-medium text-primary hover:text-primary hover:bg-muted cursor-pointer">
                        <span>{pick(lang, copy.exploreSpec)}</span>
                        <ArrowRight className="size-3.5" />
                      </Button>
                    </CardContent>
                  </Card>
                </Reveal>;
          })}
          </div>
        </div>
      </div>
    </section>;
}